import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { profileSchema, changePasswordSchema, type ProfileFormData, type ChangePasswordFormData } from '@/lib/validators';
import { useAuth } from '@/composables/auth/useAuth';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { Separator } from '@/components/ui/separator';

export function ProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: { firstName: user?.firstName || '', lastName: user?.lastName || '', phone: user?.phone || '' },
  });

  useEffect(() => {
    if (user) {
      profileForm.reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    }
  }, [user, profileForm]);

  const passwordForm = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onProfileSubmit = (data: ProfileFormData) => { updateProfile.mutate(data); };
  const onPasswordSubmit = (data: ChangePasswordFormData) => { changePassword.mutate(data, { onSuccess: () => passwordForm.reset() }); };

  return (
    <PageContainer title="Profile" description="Manage your personal information">
      <div className="space-y-6 max-w-2xl">
        <Card>
          <CardHeader><CardTitle className="text-base">Personal Information</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={profileForm} name="firstName" label="First Name" required />
                <FormField form={profileForm} name="lastName" label="Last Name" required />
              </div>
              <FormField form={profileForm} name="phone" label="Phone" />
              <div className="flex justify-end"><Button type="submit" disabled={updateProfile.isPending}>{updateProfile.isPending ? 'Saving...' : 'Save Changes'}</Button></div>
            </form>
          </CardContent>
        </Card>

        <Separator />

        <Card>
          <CardHeader><CardTitle className="text-base">Change Password</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
              <FormField form={passwordForm} name="currentPassword" label="Current Password" type="password" required />
              <FormField form={passwordForm} name="newPassword" label="New Password" type="password" required />
              <FormField form={passwordForm} name="confirmPassword" label="Confirm New Password" type="password" required />
              <div className="flex justify-end"><Button type="submit" disabled={changePassword.isPending}>{changePassword.isPending ? 'Changing...' : 'Change Password'}</Button></div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
